<template>
  <div
    class="panel"
    :class="[{ 'is-loading': loading, 'is-empty': empty, 'is-nozones': noZones, muted }]"
    :style="{ '--panel-h': resolvedHeight, '--accent': resolvedAccent }"
  >
    <div class="panel-head">
      <div class="title">
        <i v-if="icon" :class="['bi', icon]"></i>
        <div class="title-text">
          <h6>{{ title }}</h6>
          <!-- Optional one-liner naming what the number actually counts.
               Added for the zone panels, where "entries" includes people who
               walked through without stopping — a distinction the title alone
               cannot carry and whose absence made a raw count read as a
               customer count. -->
          <span v-if="subtitle" class="panel-subtitle">{{ subtitle }}</span>
          <!-- Optional calm-blue note about the figures (e.g. the "changed at this
               spot" marker). In the head, so it never takes height from the chart. -->
          <span v-if="note" class="panel-note">{{ note }}</span>
        </div>
      </div>
      <div class="tools"><slot name="tools" /></div>
    </div>

    <div class="panel-body">
      <!-- state overlay (consistent across panels) -->
      <div v-if="loading" class="state">
        <div class="loader"></div>
        <span class="state-text">{{ loadingText ?? $t('controls.state.loading') }}</span>
      </div>
      <div v-else-if="noZones" class="state">
        <i class="bi bi-exclamation-triangle state-ico"></i>
        <span class="state-text">{{ noZonesText ?? $t('controls.state.noEntranceZones') }}</span>
      </div>
      <div v-else-if="empty" class="state">
        <i class="bi bi-dash-circle state-ico"></i>
        <span class="state-text">{{ emptyText ?? $t('controls.state.noData') }}</span>
      </div>

      <!-- normal content -->
      <div v-else class="panel-slot">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  title:   { type: String, required: true },
  subtitle:{ type: String, default: '' },            // one-line clarifier under the title
  note:    { type: String, default: '' },            // calm-blue note under the subtitle
  height:  { type: [Number, String], default: 320 }, // px if number
  icon:    { type: String, default: '' },            // e.g. "bi-graph-up"
  accent:  { type: String, default: '#36910d' },
  muted:   { type: Boolean, default: false },

  // states (align with KpiCard)
  loading: { type: Boolean, default: false },
  noZones: { type: Boolean, default: false },
  empty:   { type: Boolean, default: false },

  // state messages
  // (null = the default text, in the portal language)
  loadingText: { type: String, default: null },
  noZonesText: { type: String, default: null },
  emptyText:   { type: String, default: null },
})

const resolvedHeight =
  typeof props.height === 'number' ? `${props.height}px` : props.height

// mute accent when there's nothing to show
const resolvedAccent = computed(() =>
  (props.noZones || props.empty) ? '#c7d2da' : props.accent
)
</script>

<style scoped>
.panel{
  background:#fff;
  border:1px solid #e6edf3;
  border-left:4px solid var(--accent);       /* ← accent bar */
  border-radius:12px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
  overflow:hidden;                            /* keeps left bar tidy on rounded corners */
  position:relative;
}
.panel.muted{ opacity:.65; filter:grayscale(.1) }

.panel-head{
  display:flex; justify-content:space-between; align-items:center;
  padding:10px 12px; color:#425461;
  border-bottom:1px solid #eef3f7;
}
.panel-head .title{ display:flex; align-items:center; gap:.5rem }
.panel-head .title i{ color:var(--accent); opacity:.9 }
.panel-head h6{ margin:0; font-weight:700 }
/* Title + optional clarifier stack vertically; the icon stays centred against
   the pair, so a panel without a subtitle looks exactly as it did before. */
.panel-head .title-text{ display:flex; flex-direction:column; gap:1px; min-width:0 }
.panel-head .panel-subtitle{
  font-size:.72rem; font-weight:500; color:#81909a; line-height:1.25;
}
.panel-head .panel-note{
  font-size:.72rem; font-weight:500; color:#4F7EB3; line-height:1.25;
}

.panel-body{
  position:relative;
  height: var(--panel-h, 320px);
  padding: 6px 8px 10px;
}

/* chart slot gets full space */
.panel-slot{
  width:100%; height:100%;
  min-width:0;
  /* Same trick PrioChart uses: the chart's inline size is determined by this
     box, not by the canvas inside it — breaks the circular sizing that froze
     raw vue-echarts charts (the zone deep-dive charts) on window-drag and let
     them push the panel over-wide. Harmless for PrioChart charts (they already
     contain themselves). */
  contain: inline-size;
}

/* state view (centered, consistent with KpiCard) */
.state{
  position:absolute; inset:0;
  display:flex; gap:.5rem; align-items:center; justify-content:center;
  padding: 12px 24px;          /* breathing room so long messages don't sit flush */
  color:#6b7a86;
  text-align: center;
}
.state-ico{ font-size:1.1rem; opacity:.85 }
.loader{
  width:18px; height:18px; border-radius:50%;
  background:#B2C149; padding:2px; aspect-ratio:1;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spin 1s linear infinite;
}
@keyframes spin{to{transform:rotate(1turn)}}
</style>